import { zButton } from "#app/types/schema.ts";
import { z } from "zod";
import { Link } from "react-router";
import { buttonVariants } from "./ui/button";
import { ComponentProps } from "react";
import { cn } from "#app/utils/misc.tsx";
import { resolveSanityLink } from "#app/utils/resolveLinks.ts";
import { useSiteSettings } from "#app/utils/site-settings-provider.ts";
import { Icon } from "./ui/icon";
import { EncodeDataAttributeCallback } from "@sanity/react-loader";
import { useGtm } from '#app/utils/gtm-context.tsx';

type SanityLinkProps = Omit<ComponentProps<'a'> & ComponentProps<typeof Link>, 'href' | 'to' | 'ref'> & {
    link: z.infer<typeof zButton>;
    // No need to pass siteSettings as prop if using context
    encodeDataAttribute?: EncodeDataAttributeCallback;
};

export default function SanityLink({ link, className, children, encodeDataAttribute, ...rest }: SanityLinkProps) {
    const siteSettings = useSiteSettings();
    const { trackEvent } = useGtm();
    // Don't render if no link data provided
    if (!link) return null;
    
    // Resolve the actual URL/href
    const href = resolveSanityLink(link, siteSettings);
    
    // Don't render if link can't be resolved (e.g., missing global phone/email)
    if (!href) {
        console.warn("Could not resolve href for link:", link);
        // Optionally render disabled text or nothing
        return <span className={className} {...rest}>{link.text ?? children}</span>; // Fallback span
    }

    const handleClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        // Check if the link is a phone number link
        if (href.startsWith('tel:')) {
            trackEvent('click_to_call', {
                link_text: link.text || (typeof children === 'string' ? children : ''),
                link_url: href,
                page_path: location.pathname + location.search,
                page_title: document.title,
            });
        }
        // If an onClick prop was also passed to SanityLink, call it too
        if (rest.onClick) {
            rest.onClick(event);
        }
    };
    
    // Determine style and icon
    const linkStyle = link.style ?? 'default';
    const linkSize = link.size ?? 'default';
    const iconName = link.icon && link.icon.length > 0 ? link.icon : undefined;
    const isExternal = link.linkType === 'external';
    
    // Combine button variant styles with any passed className
    const combinedClassName = cn(
        linkStyle !== 'link' ? buttonVariants({ variant: linkStyle, size: linkSize }) : 'hover:underline',
        className
    );
    
    // Prepare common props for both Link and a
    const commonProps = {
        className: combinedClassName,
        children: (
            <>
                {children ?? link.text} {/* Use children prop if provided, else button text */}
                {iconName && <Icon name={iconName} />}
            </>
        ),
        ...rest // Spread other props like onClick, aria-label, etc.
    };

    return (
        <Link
        to={href}
        prefetch={isExternal ? 'intent' : undefined}
        target={isExternal ? '_blank' : undefined}
        rel={isExternal ? 'noopener noreferrer' : undefined}
        onClick={handleClick}
        {...commonProps}
        />
    )
}